export type LocalAutomationStatus = "active" | "paused" | "archived";
export type LocalAutomationScheduleKind = "manual" | "every_minute" | "daily" | "weekly";
export type LocalAutomationRunStatus = "queued" | "running" | "succeeded" | "failed";
export type LocalAutomationRunTrigger = "manual" | "schedule";

export interface LocalAutomation {
  readonly id: string;
  readonly name: string;
  readonly prompt: string;
  readonly status: LocalAutomationStatus;
  readonly scheduleKind: LocalAutomationScheduleKind;
  readonly scheduleTime: string | null;
  readonly scheduleDayOfWeek: number | null;
  readonly timezone: string;
  readonly projectName: string | null;
  readonly model: string | null;
  readonly nextRunAt: string | null;
  readonly lastRunAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

export interface LocalAutomationRun {
  readonly id: string;
  readonly automationId: string;
  readonly triggerType: LocalAutomationRunTrigger;
  readonly status: LocalAutomationRunStatus;
  readonly threadId: string | null;
  readonly error: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly updatedAt: string;
}

export interface LocalAutomationsResponse {
  readonly automations: readonly LocalAutomation[];
  readonly runsByAutomation: Readonly<Record<string, readonly LocalAutomationRun[]>>;
}

export interface CreateLocalAutomationInput {
  readonly name: string;
  readonly prompt: string;
  readonly scheduleKind: LocalAutomationScheduleKind;
  readonly scheduleTime: string | null;
  readonly scheduleDayOfWeek: number | null;
  readonly timezone: string;
  readonly projectName: string | null;
  readonly model: string | null;
}

export type UpdateLocalAutomationInput = Partial<CreateLocalAutomationInput> & {
  readonly id: string;
  readonly status?: LocalAutomationStatus;
};

async function parseJsonResponse<T>(response: Response, fallback: string): Promise<T> {
  const body = (await response.json().catch(() => null)) as { readonly error?: string } | T | null;
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : fallback;
    throw new Error(message);
  }
  return body as T;
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...init.headers,
    },
  });
  return parseJsonResponse<T>(response, "Local automation request failed.");
}

export const localAutomationClient = {
  list(): Promise<LocalAutomationsResponse> {
    return requestJson<LocalAutomationsResponse>("/api/local-automations", {
      headers: {},
    });
  },
  create(input: CreateLocalAutomationInput): Promise<{ readonly automation: LocalAutomation }> {
    return requestJson("/api/local-automations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  update(input: UpdateLocalAutomationInput): Promise<{ readonly automation: LocalAutomation }> {
    return requestJson("/api/local-automations", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  delete(id: string): Promise<{ readonly ok: boolean }> {
    return requestJson("/api/local-automations", {
      method: "DELETE",
      body: JSON.stringify({ id }),
    });
  },
  run(id: string): Promise<{ readonly runId: string; readonly threadId: string }> {
    return requestJson("/api/local-automations/run", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },
};
